import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Plus, Pencil, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { api, adminHeaders, money, variantText } from "@/lib/api";
import { Header } from "@/components/Header";
import { VariantEditor } from "@/components/VariantEditor";
import { AddonEditor, normalizeAddons, normalizeVariants } from "@/components/AddonEditor";
import { OptionSetsTab } from "@/components/OptionSetsTab";
import { PriceAlertsTab } from "@/components/PriceAlertsTab";
import { SamplesManager } from "@/components/SamplesManager";
import { ImageUrlField } from "@/components/ImageUrlField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyProduct = {
  category: "boat",
  brand: "",
  series: "",
  option_set_id: "",
  addons: [],
  hull_width: "",
  hull_length: "",
  fit_widths: "",
  fit_lengths: "",
  name: "",
  description: "",
  note: "",
  price: 0,
  image_url: "",
  gallery: [],
  variants: [],
  details: [],
  active: true,
  sort_order: 0,
  shipping: 0,
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("fdm_admin_key"));
  const [passcode, setPasscode] = useState("");
  const [checking, setChecking] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [optionSets, setOptionSets] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [filterCat, setFilterCat] = useState("all");
  const [editing, setEditing] = useState(null); // product being edited or null
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const login = async () => {
    const code = passcode.trim();
    if (!code) {
      toast.error("Please enter the passcode.");
      return;
    }
    setChecking(true);
    try {
      localStorage.setItem("fdm_admin_key", code);
      await api.post("/admin/verify", {}, adminHeaders());
      setAuthed(true);
      toast.success("Welcome back.");
    } catch (e) {
      localStorage.removeItem("fdm_admin_key");
      if (e?.response?.status === 403) toast.error("Invalid passcode. Check for typos — it's not case-sensitive.");
      else toast.error("Couldn't reach the server. Please wait a moment and try again.");
    } finally {
      setChecking(false);
    }
  };

  const loadAll = async () => {
    try {
      const [cats, prods, qs, os, al] = await Promise.all([
        api.get("/categories"),
        api.get("/admin/products", adminHeaders()),
        api.get("/admin/quotes", adminHeaders()),
        api.get("/option-sets"),
        api.get("/admin/price-alerts", adminHeaders()),
      ]);
      setAlertCount(al.data.length);
      setCategories(cats.data);
      setProducts(prods.data);
      setQuotes(qs.data);
      setOptionSets(os.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        localStorage.removeItem("fdm_admin_key");
        setAuthed(false);
        toast.error("Session expired. Enter passcode again.");
      } else toast.error("Failed to load admin data.");
    }
  };

  useEffect(() => {
    if (authed) loadAll();
  }, [authed]);

  const openNew = () => {
    setEditing({ ...emptyProduct, category: categories[0]?.key || "boat" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing({ ...p, gallery: p.gallery || [], variants: p.variants || [], addons: p.addons || [] });
    setOpen(true);
  };

  const saveProduct = async () => {
    if (!editing.name.trim()) return toast.error("Name is required.");
    setSaving(true);
    const payload = {
      category: editing.category,
      brand: (editing.brand || "").trim(),
      series: (editing.series || "").trim(),
      option_set_id: editing.option_set_id || "",
      addons: normalizeAddons(editing.addons),
      hull_width: editing.hull_width === "" || editing.hull_width == null ? null : Number(editing.hull_width),
      hull_length: editing.hull_length === "" || editing.hull_length == null ? null : Number(editing.hull_length),
      fit_widths: String(editing.fit_widths || "").trim(),
      fit_lengths: String(editing.fit_lengths || "").trim(),
      name: editing.name,
      description: editing.description,
      note: String(editing.note || "").trim(),
      price: Number(editing.price) || 0,
      image_url: editing.image_url,
      gallery: (editing.gallery || []).map((s) => s.trim()).filter(Boolean),
      details: (editing.details || []).map((s) => s.trim()).filter(Boolean),
      variants: normalizeVariants(editing.variants),
      active: editing.active !== false,
      samples_enabled: !!editing.samples_enabled,
      sort_order: Number(editing.sort_order) || 0,
      shipping: Number(editing.shipping) || 0,
    };
    try {
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload, adminHeaders());
        toast.success("Product updated.");
      } else {
        await api.post("/admin/products", payload, adminHeaders());
        toast.success("Product added.");
      }
      setOpen(false);
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`, adminHeaders());
      toast.success("Deleted.");
      loadAll();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const catLabel = (k) => categories.find((c) => c.key === k)?.label || k;
  const shownProducts = filterCat === "all" ? products : products.filter((p) => p.category === filterCat);

  if (!authed) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="mx-auto max-w-sm px-5 py-24">
          <div className="rounded-lg border border-border bg-card p-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[#1B66C9] text-white">
              <Lock size={22} />
            </span>
            <h1 className="mt-5 font-display text-2xl font-black tracking-tight">Admin Access</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter the passcode to manage products and view quotes.
            </p>
            <div className="mt-5">
              <Label htmlFor="passcode">Passcode</Label>
              <div className="relative mt-1.5">
                <Input
                  id="passcode"
                  type={showCode ? "text" : "password"}
                  data-testid="admin-passcode-input"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                  autoCapitalize="off"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck={false}
                  className="pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid="admin-passcode-toggle"
                  onClick={() => setShowCode((s) => !s)}
                  aria-label={showCode ? "Hide passcode" : "Show passcode"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                >
                  {showCode ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              data-testid="admin-login-button"
              onClick={login}
              disabled={checking}
              className="mt-5 h-11 w-full rounded-md bg-[#1B66C9] font-bold hover:bg-[#144E9C]"
            >
              {checking ? <Loader2 className="animate-spin" size={18} /> : "Enter"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-black tracking-tight">Dashboard</h1>
          <Button
            variant="ghost"
            data-testid="admin-logout-button"
            onClick={() => {
              localStorage.removeItem("fdm_admin_key");
              setAuthed(false);
            }}
            className="text-sm font-bold text-muted-foreground"
          >
            Sign out
          </Button>
        </div>

        <Tabs defaultValue="products" className="mt-6">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="products" data-testid="tab-products">
              Products ({products.length})
            </TabsTrigger>
            <TabsTrigger value="option-sets" data-testid="tab-option-sets">
              Option Sets ({optionSets.length})
            </TabsTrigger>
            <TabsTrigger value="price-alerts" data-testid="tab-price-alerts">
              Price Alerts{alertCount > 0 && <span className="ml-1.5 rounded-full bg-[#1B66C9] px-1.5 text-[10px] font-bold text-white">{alertCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">
              Quotes ({quotes.length})
            </TabsTrigger>
          </TabsList>

          {/* Products */}
          <TabsContent value="products" className="mt-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger data-testid="product-filter-select" className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="add-product-button"
                onClick={openNew}
                className="rounded-md bg-[#1B66C9] font-bold hover:bg-[#144E9C]"
              >
                <Plus size={17} className="mr-1.5" /> Add Product
              </Button>
            </div>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60">
                  <tr className="text-left">
                    <th className="p-3 font-bold">Product</th>
                    <th className="p-3 font-bold">Category</th>
                    <th className="p-3 font-bold">Brand / Series</th>
                    <th className="p-3 font-bold">Price</th>
                    <th className="p-3 font-bold">Active</th>
                    <th className="p-3 text-right font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shownProducts.map((p) => (
                    <tr key={p.id} className="border-t border-border" data-testid={`product-row-${p.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          {p.image_url ? (
                            <img src={p.image_url} alt="" className="h-10 w-10 rounded object-cover" />
                          ) : (
                            <span className="h-10 w-10 rounded bg-secondary" />
                          )}
                          <span className="font-semibold">{p.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground">{catLabel(p.category)}</td>
                      <td className="p-3 text-muted-foreground">{[p.brand, p.series].filter(Boolean).join(" · ") || "—"}</td>
                      <td className="p-3 font-semibold">{money(p.price)}</td>
                      <td className="p-3">
                        <span className={p.active ? "text-[#16A34A]" : "text-muted-foreground"}>
                          {p.active ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <button
                            data-testid={`edit-product-${p.id}`}
                            onClick={() => openEdit(p)}
                            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            data-testid={`delete-product-${p.id}`}
                            onClick={() => deleteProduct(p)}
                            className="flex h-8 w-8 items-center justify-center rounded-md text-[#DC2626] hover:bg-secondary"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="option-sets" className="mt-5">
            <OptionSetsTab optionSets={optionSets} products={products} onChanged={loadAll} />
          </TabsContent>

          <TabsContent value="price-alerts" className="mt-5">
            <PriceAlertsTab onChanged={loadAll} />
          </TabsContent>

          {/* Quotes */}
          <TabsContent value="quotes" className="mt-5">
            <div className="space-y-4">
              {quotes.length === 0 && (
                <p className="py-10 text-center text-muted-foreground">No quotes submitted yet.</p>
              )}
              {quotes.map((q) => (
                <div key={q.id} className="rounded-lg border border-border bg-card p-5" data-testid={`quote-${q.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-display font-black">{q.quote_number}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(q.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-1 text-sm">
                        <span className="font-semibold">{q.name}</span> · {q.email}
                        {q.phone ? ` · ${q.phone}` : ""}
                      </p>
                    </div>
                    <span className="font-display text-2xl font-black text-[#1B66C9]">
                      {money(q.total)}
                    </span>
                  </div>
                  <ul className="mt-3 grid grid-cols-1 gap-1 border-t border-border pt-3 text-sm sm:grid-cols-2">
                    {q.items.map((it) => (
                      <li key={it.product_id} className="flex justify-between gap-3">
                        <span className="text-muted-foreground">
                          {it.name} {it.quantity > 1 && `× ${it.quantity}`}
                          {variantText(it.variants, it.addons) && (
                            <span className="block text-xs">{variantText(it.variants, it.addons)}</span>
                          )}
                        </span>
                        <span className="font-semibold">{money(it.price * it.quantity)}</span>
                      </li>
                    ))}
                  </ul>
                  {q.comments && (
                    <p className="mt-3 rounded-md bg-secondary/60 p-3 text-sm">
                      <span className="font-bold">Comments: </span>
                      {q.comments}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Product editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing?.id ? "Edit Product" : "Add Product"}
            </DialogTitle>
            <DialogDescription>Name, price, photos and customer-selectable options.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Name</Label>
                <Input
                  data-testid="product-name-input"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={editing.category}
                  onValueChange={(v) => setEditing({ ...editing, category: v })}
                >
                  <SelectTrigger data-testid="product-category-select" className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.key} value={c.key}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price (USD)</Label>
                <Input
                  type="number"
                  data-testid="product-price-input"
                  value={editing.price}
                  onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Brand</Label>
                <Input
                  data-testid="product-brand-input"
                  value={editing.brand || ""}
                  onChange={(e) => setEditing({ ...editing, brand: e.target.value })}
                  className="mt-1.5"
                  placeholder="Gator Trax / GatorTail / Pro-Drive"
                />
              </div>
              <div>
                <Label>Series</Label>
                <Input
                  data-testid="product-series-input"
                  value={editing.series || ""}
                  onChange={(e) => setEditing({ ...editing, series: e.target.value })}
                  className="mt-1.5"
                  placeholder="GT Series"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Shared option set</Label>
                <Select
                  value={editing.option_set_id || "none"}
                  onValueChange={(v) => setEditing({ ...editing, option_set_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="product-option-set-select" className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {optionSets.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editing.category === "boat" ? (
                <>
                  <div>
                    <Label>Hull length (ft)</Label>
                    <Input
                      type="number"
                      data-testid="product-hull-length-input"
                      value={editing.hull_length ?? ""}
                      onChange={(e) => setEditing({ ...editing, hull_length: e.target.value })}
                      className="mt-1.5"
                      placeholder="17"
                    />
                  </div>
                  <div>
                    <Label>Hull bottom width (in)</Label>
                    <Input
                      type="number"
                      data-testid="product-hull-width-input"
                      value={editing.hull_width ?? ""}
                      onChange={(e) => setEditing({ ...editing, hull_width: e.target.value })}
                      className="mt-1.5"
                      placeholder="54"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>Fits hull lengths (ft)</Label>
                    <Input
                      data-testid="product-fit-lengths-input"
                      value={editing.fit_lengths || ""}
                      onChange={(e) => setEditing({ ...editing, fit_lengths: e.target.value })}
                      className="mt-1.5"
                      placeholder="17-18 (blank = any)"
                    />
                  </div>
                  <div>
                    <Label>Fits hull widths (in)</Label>
                    <Input
                      data-testid="product-fit-widths-input"
                      value={editing.fit_widths || ""}
                      onChange={(e) => setEditing({ ...editing, fit_widths: e.target.value })}
                      className="mt-1.5"
                      placeholder="48-54 or 44,50 (blank = any)"
                    />
                  </div>
                </>
              )}
              <div className="sm:col-span-2">
                <Label>Description</Label>
                <Textarea
                  data-testid="product-description-input"
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Customer note (shown highlighted on the card, e.g. "Motor available only with a new Pro-Drive boat")</Label>
                <Input
                  data-testid="product-note-input"
                  value={editing.note || ""}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Full specs (one section per line, e.g. "The Motor · GTR40: item, item, item")</Label>
                <Textarea
                  data-testid="product-details-input"
                  value={(editing.details || []).join("\n")}
                  onChange={(e) => setEditing({ ...editing, details: e.target.value.split("\n") })}
                  className="mt-1.5 min-h-[70px] text-xs"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Main photo</Label>
                <ImageUrlField value={editing.image_url || ""} onChange={(image_url) => setEditing({ ...editing, image_url })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Spin / multi-angle gallery URLs (one per line)</Label>
                <Textarea
                  data-testid="product-gallery-input"
                  value={(editing.gallery || []).join("\n")}
                  onChange={(e) => setEditing({ ...editing, gallery: e.target.value.split("\n") })}
                  className="mt-1.5 min-h-[80px] font-mono text-xs"
                  placeholder={"https://…/angle-1.jpg\nhttps://…/angle-2.jpg"}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Add 2+ photos from different angles to power the drag-to-spin rig viewer.
                </p>
              </div>
              <div className="sm:col-span-2">
                <Label>Product-specific upgrades (color, HP, size…)</Label>
                <div className="mt-1.5">
                  <VariantEditor
                    value={editing.variants || []}
                    onChange={(variants) => setEditing({ ...editing, variants })}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label>Product-specific add-ons (checklist)</Label>
                <div className="mt-1.5">
                  <AddonEditor value={editing.addons || []} onChange={(addons) => setEditing({ ...editing, addons })} />
                </div>
              </div>
              <div className="sm:col-span-2 rounded-md border border-[#1B66C9]/30 bg-[#1B66C9]/5 p-3">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    data-testid="product-samples-enabled-checkbox"
                    checked={!!editing.samples_enabled}
                    onChange={(e) => setEditing({ ...editing, samples_enabled: e.target.checked })}
                    className="h-4 w-4 accent-[#1B66C9]"
                  />
                  Show a “Show me a sample” link on this item
                </label>
                <div className="mt-3">
                  {editing.id ? (
                    <SamplesManager productId={editing.id} />
                  ) : (
                    <p className="text-xs text-muted-foreground">Save the product first, then reopen it to upload samples.</p>
                  )}
                </div>
              </div>
              <div>
                <Label>Sort order</Label>
                <Input
                  type="number"
                  value={editing.sort_order}
                  onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Flat-rate shipping ($, added automatically)</Label>
                <Input
                  type="number"
                  step="1"
                  data-testid="product-shipping-input"
                  value={editing.shipping ?? 0}
                  onChange={(e) => setEditing({ ...editing, shipping: e.target.value })}
                  className="mt-1.5"
                  placeholder="0 = no shipping charge"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    data-testid="product-active-checkbox"
                    checked={editing.active !== false}
                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                    className="h-4 w-4 accent-[#1B66C9]"
                  />
                  Active (visible to customers)
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-product-button"
              onClick={saveProduct}
              disabled={saving}
              className="rounded-md bg-[#1B66C9] font-bold hover:bg-[#144E9C]"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
